/**
 * PP-10 — the shared look of the per-cell "expand" affordance (↗ / ⧉).
 *
 * Two-grid parity status (CLAUDE.md § "Two Results Grids — Always Check
 * Parity"): the Query Editor pair (`ResultsGrid.tsx` + `useCellRenderer.tsx`)
 * renders this INLINE per cell — its expand affordance is content-gated
 * (long/multiline/JSON values only), so per-cell buttons stay rare and cheap.
 * Table Explorer instead uses the DELEGATED
 * {@link CELL_EXPAND_OVERLAY_BUTTON_CLASS} variant below, because its
 * affordance is column-type-gated: on unsized-text tables (PG `text`, SQLite
 * `TEXT`, LOBs) EVERY cell is expandable and a mounted button per cell
 * measured ~2x slower scroll-window mounts. Both look identical to the user.
 *
 * The affordance is hover-only AND absolutely positioned inside a
 * permanently-reserved `pl-5` gutter on its wrapping span, so toggling its
 * opacity NEVER shifts the cell text/content that follows it — the gutter is
 * reserved whether or not the button is currently visible. A hover affordance
 * that reflows content is worse than one that is always visible, because the
 * row jitters under the pointer.
 *
 * Reveal is scoped to `group/cell` (set on the owning `<td>`) rather than the
 * unscoped `group`, so a future row-level `group` can't cross-trigger it.
 *
 * Lives in one module on purpose: this pattern previously existed only in
 * Table Explorer while the Query Editor kept an always-visible copy, which is
 * exactly the drift the two-grid rule exists to prevent.
 */
export const CELL_EXPAND_BUTTON_CLASS =
  'absolute left-0 top-1/2 -translate-y-1/2 inline-flex h-4 w-4 items-center justify-center ' +
  'rounded text-muted-foreground opacity-0 group-hover/cell:opacity-100 focus-visible:opacity-100 ' +
  'hover:text-foreground hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring focus-visible:ring-offset-1';

/**
 * The wrapping span for a cell that owns a {@link CELL_EXPAND_BUTTON_CLASS}
 * button. `relative` anchors the absolute button; `pl-5` is the reserved
 * gutter it occupies. No `gap-*` — the button is out of flow, so a gap would
 * only add dead space before the text.
 */
export const CELL_EXPAND_WRAPPER_CLASS = 'relative flex items-center min-w-0 pl-5';

/**
 * The DELEGATED variant of {@link CELL_EXPAND_BUTTON_CLASS}: one floating
 * button per grid, positioned over the hovered cell's reserved gutter by the
 * grid's mouseover delegation (Table Explorer). Unlike the inline variant it
 * has no hover-reveal opacity dance — the grid shows/hides it by toggling
 * `display` — and no `left/top` utilities, because the grid computes exact
 * coordinates. Exists because mounting a button + SVG icon in EVERY
 * expandable cell measured ~2x slower window mounts on unsized-text tables
 * (PG `text`, SQLite `TEXT`, LOBs), where every cell is expandable.
 */
export const CELL_EXPAND_OVERLAY_BUTTON_CLASS =
  'absolute inline-flex h-4 w-4 items-center justify-center rounded ' +
  'text-muted-foreground hover:text-foreground hover:bg-muted/60 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';
